import { useEffect, useRef, useCallback, useState } from 'react';
import * as PIXI from 'pixi.js';
import { IsoGridOverlay } from './IsoGridOverlay';
import { IsoGroundPlane } from './IsoGroundPlane';
import { IsoTokenLayer } from './IsoTokenLayer';
import { IsoLightingSystem } from './IsoLightingSystem';
import { EffectsLayer } from './EffectsLayer';
import { resetPixiTokenClicked } from './pixiTokenState';
import { useTokenStore } from '../stores/tokenStore';
import { useMapStore } from '../stores/mapStore';
import { useCampaignMapStore } from '../stores/campaignMapStore';
import { useIsoSpriteStore, ARROW_TO_FACING } from '../stores/isoSpriteStore';
import { useDMConsoleStore } from '../stores/dmConsoleStore';
import { useEffectStore } from '../stores/effectStore';
import { useCampaignStore } from '../stores/campaignStore';
import { useChatStore } from '../stores/chatStore';
import { IsoTokenRenderer } from './IsoTokenRenderer';
import { Icon } from '../components/Icon';

/**
 * IsoMapCanvas
 *
 * Isometric (HD-2D) battle view. Renders the same tokenStore/mapStore data
 * as MapCanvas but with an isometric projection.
 *
 * Tile dimensions: tileWidth = 64, tileHeight = 32 (2:1 ratio).
 * Grid default: 20x20 (matches MapCanvas).
 *
 * Click-to-move: select a token, then click a grid cell. Token walks there.
 */

interface IsoMapCanvasProps {
  gridWidth?: number;
  gridHeight?: number;
  tileWidth?: number;
  tileHeight?: number;
}

// Isometric tile dimensions — 2:1 ratio
const DEFAULT_TILE_W = 64;
const DEFAULT_TILE_H = 32;

export function IsoMapCanvas({
  gridWidth = 20,
  gridHeight = 20,
  tileWidth = DEFAULT_TILE_W,
  tileHeight = DEFAULT_TILE_H,
}: IsoMapCanvasProps) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const appRef = useRef<PIXI.Application | null>(null);
  const gridRef = useRef<IsoGridOverlay | null>(null);
  const groundRef = useRef<IsoGroundPlane | null>(null);
  const tokenLayerRef = useRef<IsoTokenLayer | null>(null);
  const lightingRef = useRef<IsoLightingSystem | null>(null);
  const effectsLayerRef = useRef<EffectsLayer | null>(null);
  const worldContainerRef = useRef<PIXI.Container | null>(null);

  // Pan state
  const isPanningRef = useRef(false);
  const panStartRef = useRef({ x: 0, y: 0, offsetX: 0, offsetY: 0 });

  // Zoom
  const zoom = useMapStore((s) => s.zoom);
  const setZoom = useMapStore((s) => s.setZoom);
  const MIN_ZOOM = 0.25;
  const MAX_ZOOM = 4;
  const ZOOM_STEP = 0.1;



  // GM tray state — controls snap above it
  const trayOpen = useDMConsoleStore((s) => s.isOpen);
  const controlsBottom = trayOpen ? 'calc(40vh + 16px)' : '60px';

  // Torch placement mode
  const [placingTorch, setPlacingTorch] = useState(false);
  const [showIsoHelp, setShowIsoHelp] = useState(false);
  const placingTorchRef = useRef(false);
  placingTorchRef.current = placingTorch;

  // Map mode toggle (shared with MapCanvas)
  const setMapMode = useCampaignMapStore((s) => s.setMapMode);

  // Sprite generation status (for overlay indicator)
  const generating = useIsoSpriteStore((s) => s.generating);

  // Load sprite cache on mount + post chat prompt for sprite generation
  useEffect(() => {
    useIsoSpriteStore.getState().loadFromCache();

    // Post sprite prompt card — skip if the most recent message is already one
    const msgs = useChatStore.getState().messages;
    const lastMsg = msgs[msgs.length - 1];
    if (!lastMsg?.isoSpritePrompt) {
      useChatStore.setState((state) => ({
        messages: [
          ...state.messages,
          {
            id: crypto.randomUUID(),
            sender: 'System',
            message: 'Isometric view activated. Generate HD-2D character sprites for the party?',
            timestamp: new Date(),
            type: 'system',
            isoSpritePrompt: true,
          },
        ],
      }));
    }
  }, []);

  const isAnyGenerating = generating.size > 0;

  // ─── Click handler: select tokens, move selected, or place torch ────
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return;

      // Capture coords before React recycles the event
      const clientX = e.clientX;
      const clientY = e.clientY;

      // Defer so Pixi pointerdown has time to set the flag
      requestAnimationFrame(() => {
        // If a token was clicked, it handled its own selection — don't interfere
        if (resetPixiTokenClicked()) return;

        const store = useTokenStore.getState();
        const world = worldContainerRef.current;
        const grid = gridRef.current;

        // Convert click to grid coords (shared by move + torch)
        const rect = canvasRef.current?.getBoundingClientRect();
        if (!rect || !world || !grid) return;
        const screenX = clientX - rect.left;
        const screenY = clientY - rect.top;
        const worldX = (screenX - world.x) / world.scale.x;
        const worldY = (screenY - world.y) / world.scale.y;
        const gridCoords = grid.screenToGrid(worldX, worldY);
        const topDownX = Math.round(gridCoords.gx) * 50;
        const topDownY = Math.round(gridCoords.gy) * 50;

        // Torch placement mode
        if (placingTorchRef.current) {
          const torchCount = Array.from(store.tokens.values()).filter(
            t => t.emitsLight && !t.combatantId
          ).length;
          store.addToken({
            name: `Torch ${torchCount + 1}`,
            position: { x: topDownX, y: topDownY },
            emitsLight: 'torch',
            visuals: {
              shape: 'square',
              size: 'tiny',
              color: '#ff9933',
              showHpBar: false,
              showConditionIcons: false,
            },
          });
          return;
        }

        // Clicked empty ground — deselect. Movement is handled by drag-to-move.
        store.clearSelection();
      });
    },
    []
  );

  // ─── Pan (right-click drag) ─────────────────────────────────────────
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button === 2) {
      e.preventDefault();
      isPanningRef.current = true;
      const world = worldContainerRef.current;
      panStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        offsetX: world?.x ?? 0,
        offsetY: world?.y ?? 0,
      };
    }
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isPanningRef.current) return;
    const world = worldContainerRef.current;
    if (!world) return;

    const dx = e.clientX - panStartRef.current.x;
    const dy = e.clientY - panStartRef.current.y;
    world.x = panStartRef.current.offsetX + dx;
    world.y = panStartRef.current.offsetY + dy;
  }, []);

  const handleMouseUp = useCallback(() => {
    isPanningRef.current = false;
  }, []);

  // ─── Wheel zoom ─────────────────────────────────────────────────────
  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
      const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom + delta));
      setZoom(newZoom);
    },
    [zoom, setZoom]
  );

  // ─── Initialize Pixi app ───────────────────────────────────────────
  useEffect(() => {
    if (!canvasRef.current) return;

    const container = canvasRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight;

    const app = new PIXI.Application({
      width,
      height,
      backgroundColor: 0x0d0b0f,
      antialias: true,
      resolution: window.devicePixelRatio || 1,
      autoDensity: true,
    });

    container.appendChild(app.view as HTMLCanvasElement);
    appRef.current = app;

    // World container holds all iso layers — pan/zoom applied here
    const world = new PIXI.Container();
    world.sortableChildren = true;
    worldContainerRef.current = world;
    app.stage.addChild(world);

    // Center the view: position (0,0) grid corner at center-top of screen
    world.x = width / 2;
    world.y = height * 0.15;

    // Ground plane (z=0)
    const ground = new IsoGroundPlane(gridWidth, gridHeight, tileWidth, tileHeight);
    groundRef.current = ground;
    ground.container.zIndex = 0;
    world.addChild(ground.container);

    // Grid overlay (z=1)
    const grid = new IsoGridOverlay(gridWidth, gridHeight, tileWidth, tileHeight);
    gridRef.current = grid;
    grid.container.zIndex = 1;
    world.addChild(grid.container);

    // Token layer (z=2+, sorted by Y)
    const tokenLayer = new IsoTokenLayer(tileWidth, tileHeight);
    tokenLayerRef.current = tokenLayer;
    tokenLayer.container.zIndex = 10;
    tokenLayer.setTicker(app.ticker);
    world.addChild(tokenLayer.container);

    // Spell effects layer (z=3, between tokens and lighting)
    // EffectsLayer stores effect origins in top-down pixel coords (50px/tile).
    // In iso view we project that space onto the iso diamond via a 2x3 matrix
    // so effects land on the right grid cells alongside tokens.
    //
    // Math: for top-down (x, y), grid coords are (x/50, y/50). Iso screen is
    // ((gx-gy) * tileW/2, (gx+gy) * tileH/2). Substituting:
    //   iso_x = (x - y) * tileW / (2 * 50)
    //   iso_y = (x + y) * tileH / (2 * 50)
    // → matrix: a = tileW/100, b = tileH/100, c = -tileW/100, d = tileH/100
    const TOP_DOWN_TILE = 50;
    const effectsLayer = new EffectsLayer(TOP_DOWN_TILE);
    effectsLayerRef.current = effectsLayer;
    effectsLayer.setTicker(app.ticker);
    effectsLayer.container.zIndex = 5;

    // Apply iso projection via rotation + Y-squash (kept as PIXI props so
    // hit tests follow the same transform the visuals do). A raw matrix write
    // does not propagate correctly to PIXI's hit test pipeline.
    //
    // Composition: scale(topDownTile/tileWidth) → rotate(45°) → scaleY(0.5)
    // For our defaults (tileWidth=64, tileHeight=32, topDownTile=50):
    //   rotate 45° + scale(tileWidth/50 * √2/2, tileHeight/50 * √2/2)
    // Simpler: use rotation and independent x/y scale so hit tests work.
    const scale = (tileWidth * Math.SQRT1_2) / TOP_DOWN_TILE;
    effectsLayer.container.rotation = Math.PI / 4; // 45°
    effectsLayer.container.scale.set(scale, scale * (tileHeight / tileWidth));

    world.addChild(effectsLayer.container);

    // Lighting — canvas-based overlay with radial gradient light holes
    const lighting = new IsoLightingSystem(width, height);
    lightingRef.current = lighting;
    lighting.setTicker(app.ticker);
    lighting.setAmbient({ r: 15, g: 12, b: 8 }, 0.5);
    lighting.setLightRadius(200);
    lighting.container.zIndex = 999;
    lighting.container.eventMode = 'none';
    lighting.container.interactiveChildren = false;
    app.stage.addChild(lighting.container);

    // Handle resize
    const handleResize = () => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      app.renderer.resize(w, h);
      lighting.resize(w, h);
    };

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(container);

    // Make stage interactive for click events
    app.stage.eventMode = 'static';
    app.stage.hitArea = app.screen;

    return () => {
      resizeObserver.disconnect();
      lighting.destroy();
      effectsLayer.destroy();
      tokenLayer.destroy();
      grid.destroy();
      ground.destroy();
      app.destroy(true);
      appRef.current = null;
      effectsLayerRef.current = null;
    };
  }, [gridWidth, gridHeight, tileWidth, tileHeight]);

  // ─── Apply zoom to world container ──────────────────────────────────
  useEffect(() => {
    const world = worldContainerRef.current;
    if (world) {
      world.scale.set(zoom, zoom);
    }
  }, [zoom]);

  // ─── Keyboard: arrow keys for facing, Escape cancels torch mode ──
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't hijack arrow keys while typing — let form elements handle cursor movement
      const active = document.activeElement as HTMLElement | null;
      const inInput = active instanceof HTMLInputElement
        || active instanceof HTMLTextAreaElement
        || !!active?.isContentEditable;

      // Escape cancels torch placement
      if (e.key === 'Escape' && placingTorchRef.current) {
        setPlacingTorch(false);
        IsoTokenRenderer.torchLabelsVisible = false;
        tokenLayerRef.current?.refreshTorchLabels();
        return;
      }

      const facing = ARROW_TO_FACING[e.key];
      if (!facing) return;
      if (inInput) return;

      const sel = useTokenStore.getState().selectedIds;
      if (sel.size === 0) {
        console.log('[IsoMap] Arrow key ignored — no token selected. Click a token first.');
        return;
      }
      if (sel.size !== 1) {
        console.log(`[IsoMap] Arrow key ignored — ${sel.size} tokens selected; arrows rotate only the single selection.`);
        return;
      }

      const tokenId = sel.values().next().value;
      if (!tokenId) return;
      const token = useTokenStore.getState().tokens.get(tokenId);
      if (!token) return;

      const spriteId = token.combatantId ?? token.id;
      if (!spriteId) {
        console.log('[IsoMap] Arrow key ignored — token has no combatantId/id');
        return;
      }
      e.preventDefault();
      useIsoSpriteStore.getState().setFacing(spriteId, facing);
      console.log(`[IsoMap] Facing set: ${token.name} → ${facing}`);
    };

    // Capture phase so we win over any other listener that might swallow arrows
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, []);

  // Subscribe to spell effect broadcasts (realtime sync)
  const currentCampaignId = useCampaignStore((s) => s.currentCampaignId);
  useEffect(() => {
    if (!currentCampaignId) return;
    useEffectStore.getState().subscribe(currentCampaignId);
    return () => { useEffectStore.getState().unsubscribe(); };
  }, [currentCampaignId]);

  // Prevent context menu on right-click
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
  }, []);

  return (
    <div
      ref={canvasRef}
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        cursor: isPanningRef.current ? 'grabbing' : 'default',
      }}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onWheel={handleWheel}
    >
      {/* UI overlay — fixed position to float above GM Tray (z-900) */}
      <div style={{ position: 'fixed', top: 0, bottom: 0, left: 280, right: 320, zIndex: 950, pointerEvents: 'none' }}>

      {/* Zoom controls */}
      <div
        style={{
          position: 'absolute',
          bottom: controlsBottom,
          right: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          zIndex: 100,
          backgroundColor: 'rgba(10, 8, 12, 0.9)',
          borderRadius: 6,
          padding: 2,
          border: '1px solid var(--color-border)',
          pointerEvents: 'auto',
        }}
      >
        <button
          onClick={() => setZoom(Math.min(MAX_ZOOM, zoom + ZOOM_STEP))}
          title="Zoom In"
          style={{
            width: 32, height: 32, borderRadius: 4, border: 'none',
            backgroundColor: 'transparent', color: 'var(--color-text-secondary)',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Icon name="plus" size={16} />
        </button>
        <button
          onClick={() => setZoom(1)}
          title="Reset Zoom"
          style={{
            width: 32, height: 32, borderRadius: 4, border: 'none',
            backgroundColor: zoom !== 1 ? 'rgba(212, 175, 55, 0.15)' : 'transparent',
            color: zoom !== 1 ? '#d4af37' : 'var(--color-text-secondary)',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: 'Georgia, serif', fontSize: 10,
          }}
        >
          {Math.round(zoom * 100)}%
        </button>
        <button
          onClick={() => setZoom(Math.max(MIN_ZOOM, zoom - ZOOM_STEP))}
          title="Zoom Out"
          style={{
            width: 32, height: 32, borderRadius: 4, border: 'none',
            backgroundColor: 'transparent', color: 'var(--color-text-secondary)',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Icon name="minus" size={16} />
        </button>
      </div>

      {/* Tools — torch placement + info */}
      <div
        style={{
          position: 'absolute',
          bottom: `calc(${controlsBottom} + 164px)`,
          right: 16,
          display: 'flex',
          gap: 2,
          zIndex: 100,
          backgroundColor: 'rgba(10, 8, 12, 0.9)',
          borderRadius: 6,
          padding: 2,
          border: '1px solid var(--color-border)',
          pointerEvents: 'auto',
        }}
      >
        <button
          onClick={() => {
            const next = !placingTorch;
            setPlacingTorch(next);
            IsoTokenRenderer.torchLabelsVisible = next;
            tokenLayerRef.current?.refreshTorchLabels();
          }}
          title={placingTorch ? 'Stop placing torches (Esc)' : 'Place Torch'}
          style={{
            width: 32, height: 32, borderRadius: 4, border: 'none',
            backgroundColor: placingTorch ? 'rgba(255, 153, 51, 0.3)' : 'transparent',
            color: placingTorch ? '#ff9933' : 'var(--color-text-secondary)',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 16,
          }}
        >
          &#x1F525;
        </button>
        <button
          onClick={() => setShowIsoHelp(!showIsoHelp)}
          title="Iso View Controls"
          style={{
            width: 32, height: 32, borderRadius: 4, border: 'none',
            backgroundColor: showIsoHelp ? 'rgba(212, 175, 55, 0.2)' : 'transparent',
            color: showIsoHelp ? '#d4af37' : 'var(--color-text-secondary)',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Icon name="question" size={14} />
        </button>
      </div>

      {/* Torch placement hint */}
      {placingTorch && (
        <div
          style={{
            position: 'absolute',
            bottom: `calc(${controlsBottom} + 204px)`,
            right: 16,
            zIndex: 100,
            fontFamily: 'Georgia, serif',
            fontSize: 11,
            color: '#ff9933',
            backgroundColor: 'rgba(10, 8, 12, 0.85)',
            padding: '4px 10px',
            borderRadius: 4,
            border: '1px solid rgba(255, 153, 51, 0.3)',
            pointerEvents: 'none',
          }}
        >
          Click to place torch &middot; Esc to cancel
        </div>
      )}

      {/* Iso help panel */}
      {showIsoHelp && (
        <div
          style={{
            position: 'absolute',
            bottom: `calc(${controlsBottom} + 204px)`,
            right: 16,
            zIndex: 100,
            fontFamily: 'Georgia, serif',
            fontSize: 11,
            color: 'var(--color-text-secondary)',
            backgroundColor: 'rgba(10, 8, 12, 0.92)',
            padding: '8px 12px',
            borderRadius: 6,
            border: '1px solid rgba(212, 175, 55, 0.2)',
            pointerEvents: 'auto',
            lineHeight: 1.6,
            width: 200,
          }}
        >
          <div style={{ color: '#d4af37', fontWeight: 'bold', marginBottom: 4, fontSize: 12 }}>
            Iso View Controls
          </div>
          <div><b style={{ color: '#e0d5c0' }}>Click token</b> then click ground to move</div>
          <div><b style={{ color: '#e0d5c0' }}>Arrow keys</b> change facing direction</div>
          <div><b style={{ color: '#e0d5c0' }}>Scroll</b> zoom in/out</div>
          <div><b style={{ color: '#e0d5c0' }}>Middle-drag</b> pan the map</div>
          <div><b style={{ color: '#e0d5c0' }}>&#x1F525; button</b> place torches (click map)</div>
          <div><b style={{ color: '#e0d5c0' }}>Esc</b> cancel placement</div>
        </div>
      )}

      {/* Mode toggle — back to top-down */}
      <div
        style={{
          position: 'absolute',
          bottom: `calc(${controlsBottom} + 114px)`,
          right: 16,
          display: 'flex',
          gap: 2,
          zIndex: 100,
          backgroundColor: 'rgba(10, 8, 12, 0.9)',
          borderRadius: 6,
          padding: 2,
          border: '1px solid var(--color-border)',
          pointerEvents: 'auto',
        }}
      >
        <button
          onClick={() => setMapMode('campaign')}
          title="Campaign Map"
          style={{
            width: 32, height: 32, borderRadius: 4, border: 'none',
            backgroundColor: 'transparent', color: 'var(--color-text-secondary)',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Icon name="map" size={16} />
        </button>
        <button
          onClick={() => setMapMode('battle')}
          title="Battle Map (Top-Down)"
          style={{
            width: 32, height: 32, borderRadius: 4, border: 'none',
            backgroundColor: 'transparent', color: 'var(--color-text-secondary)',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Icon name="sword" size={16} />
        </button>
        <button
          onClick={() => setMapMode('iso')}
          title="Isometric View"
          style={{
            width: 32, height: 32, borderRadius: 4, border: 'none',
            backgroundColor: 'rgba(212, 175, 55, 0.25)', color: '#d4af37',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Icon name="diamond" size={16} />
        </button>
      </div>

      {/* Iso view label */}
      <div
        style={{
          position: 'absolute',
          top: 12,
          left: 12,
          zIndex: 100,
          fontFamily: 'Georgia, serif',
          fontSize: 11,
          color: '#d4af37',
          opacity: 0.6,
          letterSpacing: 2,
          textTransform: 'uppercase',
          pointerEvents: 'none',
        }}
      >
        Isometric View
      </div>

      {/* Sprite status — small indicator only, main button is in chat */}
      {isAnyGenerating && (
        <div
          style={{
            position: 'absolute',
            top: 12,
            right: 16,
            zIndex: 100,
            fontFamily: 'Georgia, serif',
            fontSize: 11,
            color: '#d4af37',
            backgroundColor: 'rgba(10, 8, 12, 0.85)',
            padding: '4px 10px',
            borderRadius: 4,
            border: '1px solid rgba(212, 175, 55, 0.3)',
            pointerEvents: 'none',
          }}
        >
          Rendering {Array.from(generating)[0]}...
        </div>
      )}

      {/* Click-to-move + facing hint */}

      </div>{/* end UI overlay */}
    </div>
  );
}
